import { useCallback, useEffect, useRef, useState } from 'react';
import type { Block } from '@/components/canvas/blocks/types';
import { getBlockMinSize } from '@/components/canvas/blocks/registry/blockRegistry';
import { useBlockResize } from '@/components/canvas/blocks/useBlockResize';
import {
  computeDragSnap,
  type AlignmentGuideLine,
} from '@/components/canvas/transform/alignmentGuides';
import type { PageSnapContext } from '@/components/canvas/transform/useElementDrag';
import type { ResizeHandle } from '@/components/canvas/transform';

/** 超过该像素位移才视为拖拽，避免点击选中时块体跟手 */
const BLOCK_DRAG_THRESHOLD_PX = 4;

export interface UseBlockInteractionOptions {
  blocks: Block[];
  zoom: number;
  pageSnapRef: React.MutableRefObject<PageSnapContext>;
  selectedBlockIds: string[];
  selectBlock: (id: string, addToSelection?: boolean) => void;
  clearSelection: () => void;
  setBlockPositions: (updates: { id: string; x: number; y: number }[]) => void;
  updateBlockSilent: (
    id: string,
    patch: import('@/components/canvas/blocks/types').BlockPatch,
  ) => void;
  commitBlockLayout: (
    id: string,
    layout: { x: number; y: number; width: number; height: number },
  ) => void;
  pushHistory: () => void;
  cancelPendingHistory: () => void;
  isPresentationMode: boolean;
  isPanModeActive: boolean;
}

export function useBlockInteraction(options: UseBlockInteractionOptions) {
  const {
    blocks,
    zoom,
    pageSnapRef,
    selectedBlockIds,
    selectBlock,
    setBlockPositions,
    updateBlockSilent,
    commitBlockLayout,
    pushHistory,
    cancelPendingHistory,
    isPresentationMode,
    isPanModeActive,
  } = options;

  const [blockDrag, setBlockDrag] = useState<{
    startClientX: number;
    startClientY: number;
    startPositions: { id: string; x: number; y: number }[];
  } | null>(null);
  const [blockDragOffset, setBlockDragOffset] = useState({ dx: 0, dy: 0 });
  const [blockAlignmentGuides, setBlockAlignmentGuides] = useState<AlignmentGuideLine[]>([]);
  const [pointerSession, setPointerSession] = useState(0);

  const blockDragOffsetRef = useRef({ dx: 0, dy: 0 });
  blockDragOffsetRef.current = blockDragOffset;

  const selectedSet = useRef(new Set(selectedBlockIds));
  selectedSet.current = new Set(selectedBlockIds);

  const blocksRef = useRef(blocks);
  blocksRef.current = blocks;

  const pendingDragRef = useRef<{
    pointerId: number;
    startClientX: number;
    startClientY: number;
    startPositions: { id: string; x: number; y: number }[];
  } | null>(null);

  const { resizeState, setResizeState, resizePreview } = useBlockResize(
    zoom,
    updateBlockSilent,
    commitBlockLayout,
  );

  useEffect(() => {
    const pending = pendingDragRef.current;
    if (!pending || pointerSession === 0) return;

    const scale = zoom > 0 ? zoom : 1;
    let dragStarted = false;
    let historyPushed = false;

    const applySnap = (rawDx: number, rawDy: number) => {
      const ctx = pageSnapRef.current;
      const snapped = computeDragSnap({
        startPositions: pending.startPositions,
        rawDx,
        rawDy,
        elements: ctx.elements,
        blocks: ctx.blocks,
        pageWidth: ctx.pageWidth,
        pageHeight: ctx.pageHeight,
        gridGuides: ctx.guides,
        zoom: ctx.zoom,
      });
      blockDragOffsetRef.current = { dx: snapped.dx, dy: snapped.dy };
      setBlockDragOffset({ dx: snapped.dx, dy: snapped.dy });
      setBlockAlignmentGuides(snapped.guides);
    };

    const onMove = (ev: PointerEvent) => {
      if (ev.pointerId !== pending.pointerId) return;
      const rawDx = ev.clientX - pending.startClientX;
      const rawDy = ev.clientY - pending.startClientY;
      if (!dragStarted) {
        if (Math.hypot(rawDx, rawDy) < BLOCK_DRAG_THRESHOLD_PX) return;
        dragStarted = true;
        historyPushed = true;
        pushHistory();
        setBlockDrag({
          startClientX: pending.startClientX,
          startClientY: pending.startClientY,
          startPositions: pending.startPositions,
        });
        setBlockAlignmentGuides([]);
      }
      applySnap(rawDx / scale, rawDy / scale);
    };

    const onUp = (ev: PointerEvent) => {
      if (ev.pointerId !== pending.pointerId) return;

      if (dragStarted) {
        const { dx, dy } = blockDragOffsetRef.current;
        if (dx !== 0 || dy !== 0) {
          setBlockPositions(
            pending.startPositions.map((s) => ({
              id: s.id,
              x: s.x + dx,
              y: s.y + dy,
            })),
          );
        } else if (historyPushed) {
          cancelPendingHistory();
        }
      }

      pendingDragRef.current = null;
      setBlockDrag(null);
      setBlockDragOffset({ dx: 0, dy: 0 });
      setBlockAlignmentGuides([]);
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onUp);
    };

    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onUp);
    return () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onUp);
    };
  }, [pointerSession, zoom, pageSnapRef, pushHistory, cancelPendingHistory, setBlockPositions]);

  const handleBlockHitPointerDown = useCallback(
    (e: React.PointerEvent, block: Block) => {
      if (isPresentationMode || isPanModeActive) return;
      if (e.button !== 0) return;
      e.stopPropagation();

      let startPositions: { id: string; x: number; y: number }[];
      if (selectedSet.current.has(block.id)) {
        startPositions = blocksRef.current
          .filter((b) => selectedSet.current.has(b.id))
          .map((b) => ({ id: b.id, x: b.layout.x, y: b.layout.y }));
      } else {
        selectBlock(block.id, false);
        startPositions = [{ id: block.id, x: block.layout.x, y: block.layout.y }];
      }

      pendingDragRef.current = {
        pointerId: e.pointerId,
        startClientX: e.clientX,
        startClientY: e.clientY,
        startPositions,
      };
      setBlockDrag(null);
      setBlockDragOffset({ dx: 0, dy: 0 });
      setBlockAlignmentGuides([]);
      setPointerSession((n) => n + 1);
    },
    [isPresentationMode, isPanModeActive, selectBlock],
  );

  const startBlockResize = useCallback(
    (block: Block, handle: ResizeHandle, e: React.MouseEvent) => {
      if (isPresentationMode) return;
      e.stopPropagation();
      e.preventDefault();
      pushHistory();
      const layout = block.layout;
      const min = getBlockMinSize(block.type);
      setResizeState({
        id: block.id,
        handle,
        startX: layout.x,
        startY: layout.y,
        startW: layout.width,
        startH: layout.height,
        startClientX: e.clientX,
        startClientY: e.clientY,
        minWidth: min.width,
        minHeight: min.height,
      });
    },
    [isPresentationMode, pushHistory, setResizeState],
  );

  return {
    handleBlockHitPointerDown,
    handleBlockHitMouseDown: handleBlockHitPointerDown,
    blockDrag,
    blockDragOffset,
    blockAlignmentGuides,
    blockResizeState: resizeState,
    blockResizePreview: resizePreview,
    startBlockResize,
  };
}

export type BlockInteractionApi = ReturnType<typeof useBlockInteraction>;
